import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useToast } from "@/hooks/use-toast";
import { Calendar as CalendarIcon, StickyNote, Loader2, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { format } from "date-fns";

interface ClientNotesProps {
  clientId: string;
}

type ClientNote = {
  id: string;
  client_id: string;
  content: string;
  reminder_at: string | null;
  created_at: string;
  created_by?: string | null;
  updated_by?: string | null;
  highlighted: boolean;
};

export default function ClientNotes({ clientId }: ClientNotesProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [content, setContent] = useState("");
  const [withReminder, setWithReminder] = useState(false);
  const [reminderDate, setReminderDate] = useState<Date | undefined>(undefined);
  const [highlighted, setHighlighted] = useState(false);

  // Editing state
  const [editingNote, setEditingNote] = useState<ClientNote | null>(null);
  const [editContent, setEditContent] = useState("");
  const [editWithReminder, setEditWithReminder] = useState(false);
  const [editReminderDate, setEditReminderDate] = useState<Date | undefined>(undefined);
  const [editHighlighted, setEditHighlighted] = useState(false);

  // Delete confirmation state
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ["client-notes", clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_notes")
        .select("*")
        .eq("client_id", clientId)
        .order("highlighted", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as ClientNote[];
    },
    enabled: !!clientId,
  });

  const addNote = useMutation({
    mutationFn: async () => {
      if (!content.trim()) throw new Error("Το κείμενο είναι υποχρεωτικό");
      
      // Get current user for created_by field
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");
      
      const payload: any = {
        client_id: clientId,
        content: content.trim(),
        reminder_at: withReminder && reminderDate ? reminderDate.toISOString() : null,
        highlighted,
        created_by: user.id, // Add created_by field for RLS policy
      };
      
      console.log("DEBUG: Inserting client note", payload);
      
      const { error } = await supabase.from("client_notes").insert([payload]);
      if (error) {
        console.error("Client note insert error:", error);
        throw error;
      }
    },
    onSuccess: () => {
      setContent("");
      setWithReminder(false);
      setReminderDate(undefined);
      setHighlighted(false);
      setIsAddOpen(false);
      queryClient.invalidateQueries({ queryKey: ["client-notes", clientId] });
      queryClient.invalidateQueries({ queryKey: ["client-notes-reminders"] });
      toast({ title: "Αποθηκεύτηκε", description: "Η σημείωση προστέθηκε." });
    },
    onError: (err: any) => {
      toast({ title: "Σφάλμα", description: err.message || "Αποτυχία προσθήκης", variant: "destructive" });
    },
  });

  const updateNote = useMutation({
    mutationFn: async (payload: { id: string; content: string; reminder_at: string | null; highlighted: boolean }) => {
      if (!payload.content.trim()) throw new Error("Το κείμενο είναι υποχρεωτικό");
      const { error } = await supabase
        .from("client_notes")
        .update({ content: payload.content.trim(), reminder_at: payload.reminder_at, highlighted: payload.highlighted })
        .eq("id", payload.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-notes", clientId] });
      queryClient.invalidateQueries({ queryKey: ["client-notes-reminders"] });
      setEditingNote(null);
      toast({ title: "Αποθηκεύτηκε", description: "Η σημείωση ενημερώθηκε." });
    },
    onError: (err: any) => {
      toast({ title: "Σφάλμα", description: err.message || "Αποτυχία ενημέρωσης", variant: "destructive" });
    },
  });

  const removeNote = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("client_notes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-notes", clientId] });
      queryClient.invalidateQueries({ queryKey: ["client-notes-reminders"] });
      setConfirmDeleteId(null);
      toast({ title: "Διαγράφηκε", description: "Η σημείωση διαγράφηκε." });
    },
    onError: (err: any) => {
      toast({ title: "Σφάλμα", description: err.message || "Αποτυχία διαγραφής", variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <StickyNote className="h-5 w-5" /> Notes
        </CardTitle>
        <Button onClick={() => setIsAddOpen(true)}>Add Note</Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add via dialog - use the button in header */}

        {/* Notes List */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="text-sm text-muted-foreground">Φόρτωση σημειώσεων...</div>
          ) : notes.length === 0 ? (
            <div className="text-sm text-muted-foreground">Δεν υπάρχουν σημειώσεις.</div>
          ) : (
            notes.map((note) => (
              <div key={note.id} className={`p-4 rounded-lg border ${note.highlighted ? "border-primary/50 bg-primary/5" : "bg-card"}`}>
                <div className="flex justify-between items-start gap-4">
                  <div className="whitespace-pre-wrap text-sm">{note.content}</div>
                  <div className="text-xs text-muted-foreground text-right min-w-[160px]">
                    <div>{format(new Date(note.created_at), "MMM dd, yyyy")}</div>
                    {note.reminder_at && (
                      <div className="mt-1">
                        <span className="font-medium">Reminder:</span> {format(new Date(note.reminder_at), "PPP")}
                      </div>
                    )}
                    <div className="flex items-center justify-end gap-1 mt-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setEditingNote(note);
                          setEditContent(note.content);
                          const dateVal = note.reminder_at ? new Date(note.reminder_at) : undefined;
                          setEditReminderDate(dateVal);
                          setEditWithReminder(!!dateVal);
                          setEditHighlighted(!!note.highlighted);
                        }}
                        aria-label="Επεξεργασία σημείωσης"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setConfirmDeleteId(note.id)}
                        aria-label="Διαγραφή σημείωσης"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      {/* Add Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent aria-describedby="add-note-desc">
          <DialogHeader>
            <DialogTitle>Νέα σημείωση</DialogTitle>
            <DialogDescription id="add-note-desc">Πρόσθεσε το κείμενο, το reminder και το highlight.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              placeholder="Πρόσθεσε σημείωση για τον πελάτη..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
            />
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Switch checked={withReminder} onCheckedChange={setWithReminder} id="add-reminder" />
                <label htmlFor="add-reminder" className="text-sm">Προσθήκη reminder</label>
              </div>
              {withReminder && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[220px] justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4 opacity-70" />
                      {reminderDate ? format(reminderDate, "PPP") : "Επιλογή ημερομηνίας"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 z-50 bg-popover" align="start">
                    <Calendar
                      mode="single"
                      selected={reminderDate}
                      onSelect={setReminderDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              )}
              <div className="flex items-center gap-2">
                <Switch checked={highlighted} onCheckedChange={setHighlighted} id="add-highlight-note" />
                <label htmlFor="add-highlight-note" className="text-sm">Highlight</label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddOpen(false)}>Άκυρο</Button>
            <Button onClick={() => addNote.mutate()} disabled={addNote.isPending || !content.trim()}>
              {addNote.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Αποθήκευση
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingNote} onOpenChange={(open) => { if (!open) setEditingNote(null); }}>
        <DialogContent aria-describedby="edit-note-desc">
          <DialogHeader>
            <DialogTitle>Επεξεργασία σημείωσης</DialogTitle>
            <DialogDescription id="edit-note-desc">Τροποποίησε το κείμενο ή το reminder.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={4}
            />
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Switch checked={editWithReminder} onCheckedChange={setEditWithReminder} id="edit-add-reminder" />
                <label htmlFor="edit-add-reminder" className="text-sm">Προσθήκη reminder</label>
              </div>
              {editWithReminder && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[220px] justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4 opacity-70" />
                      {editReminderDate ? format(editReminderDate, "PPP") : "Επιλογή ημερομηνίας"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 z-50 bg-popover" align="start">
                    <Calendar
                      mode="single"
                      selected={editReminderDate}
                      onSelect={setEditReminderDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              )}
              <div className="flex items-center gap-2">
                <Switch checked={editHighlighted} onCheckedChange={setEditHighlighted} id="edit-highlight-note" />
                <label htmlFor="edit-highlight-note" className="text-sm">Highlight</label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingNote(null)}>Άκυρο</Button>
            <Button
              onClick={() => {
                if (!editingNote) return;
                const reminder_at = editWithReminder && editReminderDate ? editReminderDate.toISOString() : null;
                updateNote.mutate({ id: editingNote.id, content: editContent.trim(), reminder_at, highlighted: editHighlighted });
              }}
              disabled={updateNote.isPending || !editContent.trim()}
            >
              {updateNote.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Αποθήκευση
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!confirmDeleteId} onOpenChange={(open) => { if (!open) setConfirmDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Διαγραφή σημείωσης;</AlertDialogTitle>
            <AlertDialogDescription>Η ενέργεια δεν μπορεί να αναιρεθεί.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Άκυρο</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (confirmDeleteId) removeNote.mutate(confirmDeleteId); }} disabled={removeNote.isPending}>
              {removeNote.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Διαγραφή
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </CardContent>
    </Card>
  );
}
